import {
  FilledInputProps,
  InputProps,
  OutlinedInputProps,
} from '@material-ui/core';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { AsyncState } from 'react-use/lib/useAsyncFn';

export interface CustomSelectedValues {
  tableValues: TextFieldValue[];
  arbitraryValues?: TextFieldValue[];
}

export enum KindEnum {
  arbitrary = 'Arbitrary',
  system = 'System',
  domain = 'Domain',
  resource = 'Resource',
  consumable = 'Consumable',
  dataContract = 'DataContract',
  remote = 'Remote',
  policy = 'Policy',
  user = 'User',
  group = 'Group',
  businessConcept = 'BusinessConcept',
}

export interface TextFieldValue {
  kind: KindEnum;
  label: string;
  isAiGenerated?: boolean;
  value?: string | GenericEntityType;
  kindDisplayName?: string;
  treePath?: string[];
}

export type Pagination = {
  limit: number;
  currentPage: number;
  cursor?: string;
  countlessOptions?: { hasNextPage: () => boolean; isFirstPage: () => boolean };
};

export type TemplateColumn = {
  name: string;
} & (
  | {
      path: string;
    }
  | {
      value: string;
    }
);

export type Column = {
  name: string;
  path: string;
  type?: string;
  renderCell?: (row: GenericEntityType) => ReactNode;
};
export interface GenericProviderType<T, F> {
  columns: Column[];
  entitiesState: AsyncState<T>;
  filters: F;
  changeFilters: <K extends keyof F>(key: K, value: F[K]) => void;
  resetFilters: () => void;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  count: number;
  nextCursor?: string;
  prevCursor?: string;
  templateKind?: TemplateEntity;
  /**
   * When present, the picker renders in hierarchical tree mode instead of a flat paginated list.
   * The tree is expanded lazily: expanding a node calls `onExpand` to fetch its children.
   */
  treeMode?: {
    /** Returns the children already loaded in memory for a given row. */
    getRowChildren: (row: GenericEntityType) => GenericEntityType[];
    /**
     * Returns the "entity ID" (IRI) for selection comparison.
     * Separate from the tree-position row ID so multi-parent nodes can be selected regardless
     * of which branch they appear in.
     */
    getEntityId: (row: GenericEntityType) => string;
    /** Called when the user expands a node; should trigger an async child fetch. */
    onExpand: (rowId: string) => void;
    /** Called when the user clicks "Load more" on a node with additional children. */
    onLoadMoreChildren: (parentId: string) => void;
    /** Row IDs currently loading their children. */
    loadingRowsIds: string[];
    /** Per-node pagination info used to decide whether to show a "Load more" sentinel. */
    rowChildrenPagination: Map<
      string,
      { totalChildren: number; loadedChildren: number; hasNextPage: boolean }
    >;
    /**
     * Returns whether a row is known to have children (even before they are loaded).
     * Used to show the expand toggle before the first expansion.
     */
    hasChildren: (row: GenericEntityType) => boolean;
  };
}

/**
 * Represents an entity that has been adapted to a common shape with name, kind, label, urn, and ref.
 */
export interface AdaptedEntity {
  name: string;
  kind: KindEnum;
  label: string;
  ref: string;
  urn: string;
  treePath?: string[];
}

export interface Provider<T, F> {
  useContext: () => GenericProviderType<T, F>;
  renderContextProvider: (
    templateKind: TemplateEntity | undefined,
    children: ReactNode,
    parentTemplateRef?: string,
    formContext?: any,
  ) => JSX.Element;
  filtersSection: () => JSX.Element;
  /**
   * Resolve entity ref/urn or throw error if no entity id is found.
   *
   * @param ref - Entity ref or urn .
   * @returns
   * - `undefined`: If the ref is rejected, the entity provider cannot process it.
   * - `NotFoundException`: If the ref is handled but no entity is found
   * - An instance of @link {TextFieldValue} if the entity is found
   *
   * @throws {NotFoundException} If the reference was handled but entity doesn't exist.
   */
  resolve: (ref: string) => Promise<TextFieldValue | undefined>;
  /**
   * Adapts a raw entity retrieved from this provider to a common shape with name, kind, label, urn, and ref.
   */
  adapt: (entity: GenericEntityType) => AdaptedEntity;
  /**
   * Optional function to extract a unique identifier from a given entity.
   * This ID is used for comparing entities instead of relying on isEqual from lodash
   * TODO make it mandatory and be sure to support all providers return types etc.
   * @param entity - The entity object to extract the ID from.
   * @returns The unique ID as a string, or undefined if not available.
   */
  extractEntityId?: (entity: GenericEntityType) => string | undefined;
  /**
   * Optional function to fetch the ancestor path (root → leaf) for a given entity ref.
   * Used by "Your selection" to lazily load the tree hierarchy when the user clicks a chip.
   * @returns An array of ancestor labels from root to the entity itself, or undefined if not available.
   */
  fetchAncestors?: (ref: string) => Promise<string[] | undefined>;
}
export interface GenericEntityType {
  __metadata: {
    name: string;
    kind: KindEnum;
  };
  [key: string]: any;
}

export enum ReturnFieldEnum {
  urn = 'urn',
  full = 'full',
  ref = 'ref',
  /**
   * Returns the RDF/SKOS IRI of the entity. Only supported by the BusinessConcept provider.
   */
  iri = 'iri',
}

/**
 * The base configuration fields for each entity provider.
 */
export interface TemplateEntity {
  type: string;
  displayName?: string;
  returnField?: ReturnFieldEnum;
  displayField?: string;
  userFilters?: string[];
  filter?: Record<string, any>;
  columns?: TemplateColumn[];
  apiSpec?: {
    retrieval?: {
      microserviceId?: string;
      baseUrl?: string;
      path: string;
      method?: string;
      params?: Record<string, any>;
    };
  };
  wittySuggestions?: { enabled: boolean };
  fieldsToSave?: string[];
  /**
   * Provider-specific extension point. Each provider can read its own custom
   * fields from this interface without requiring changes to the generic picker.
   * Use a local type cast inside the provider to access provider-specific keys.
   */
  [key: string]: unknown;
}

export type BaseProps = {
  InputProps?:
    | Partial<InputProps>
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | undefined;
};
